import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Button,
  HStack,
  Badge,
  IconButton,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Select,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Flex,
  Spacer,
  Avatar,
  List,
  ListItem,
  Divider,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiTrash2, FiUsers, FiUserPlus } from 'react-icons/fi';
import DataTable, { Column } from '@/components/common/DataTable';
import UserAutocomplete, { User as AutocompleteUser } from '@/components/common/UserAutocomplete';
import apiClient, { UserRole, TenantCreateParams, TenantUpdateParams } from '@/services/letsparkyClient';
// import { Tenant } from '@letsparky/api-v2-client';
type Tenant = any;
type User = any;
import { useAuthStore } from '@/stores/authStore';
import { useForm } from 'react-hook-form';
import { EntityStatus } from '@letsparky/api-v2-client';


interface TenantModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenant?: Tenant;
  onSave: (data: TenantUpdateParams) => Promise<void>;
  isLoading: boolean;
}

interface TenantAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenant: Tenant;
  admins: User[];
  onAssignAdmin: (userId: string) => Promise<void>;
  isLoading: boolean;
  availableUsers: User[];
}

const TenantAdminModal: React.FC<TenantAdminModalProps> = ({
  isOpen,
  onClose,
  tenant,
  admins,
  onAssignAdmin,
  isLoading,
  availableUsers,
}) => {
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      setSelectedUserId('');
    }
  }, [isOpen]);

  const handleAssign = async () => {
    if (!selectedUserId) return;
    await onAssignAdmin(selectedUserId);
    setSelectedUserId(''); // Clear selection after assignment
  };

  const handleUserSelect = (userId: string, user?: AutocompleteUser) => {
    setSelectedUserId(userId);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Manage Tenant Admins - {tenant.name}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box mb={6}>
            <Heading size="sm" mb={4}>Current Administrators</Heading>
            {admins.length > 0 ? (
              <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
                <Table variant="simple" size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Admin</Th>
                      <Th>Email</Th>
                      <Th>Phone</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {admins.map((admin) => (
                      <Tr key={admin.id}>
                        <Td>
                          <Flex align="center">
                            <Avatar 
                              size="sm" 
                              name={`${admin.name || ''} ${admin.surname || ''}`} 
                              src={admin.photo} 
                              mr={2} 
                            />
                            <Box>
                              <Text fontWeight="medium">{admin.name} {admin.surname || ''}</Text>
                            </Box>
                          </Flex>
                        </Td>
                        <Td>{admin.email}</Td>
                        <Td>{admin.phone_number || '-'}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            ) : (
              <Box p={4} bg="gray.50" borderRadius="md" textAlign="center">
                <Text color="gray.500">No administrators assigned to this tenant</Text>
                <Text fontSize="sm" mt={1}>Use the form below to assign an administrator</Text>
              </Box>
            )}
          </Box>
          
          <Divider my={6} />
          
          <Box>
            <Heading size="sm" mb={4}>Assign New Administrator</Heading>
            <VStack align="stretch" spacing={4}>
              <FormControl isRequired>
                <FormLabel>Select User</FormLabel>
                <UserAutocomplete
                  users={availableUsers.map(user => ({
                    id: user.id,
                    name: user.name,
                    surname: user.surname,
                    email: user.email,
                    phone_number: user.phone_number,
                    role: user.role,
                    photo: user.photo,
                  }))}
                  value={selectedUserId}
                  onChange={handleUserSelect}
                  placeholder="Search users to assign as administrator..."
                  isLoading={false}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Only users who are not already administrators of this tenant are shown
                </Text>
              </FormControl>
            </VStack>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Close
          </Button>
          <Button
            colorScheme="blue"
            isDisabled={!selectedUserId}
            onClick={handleAssign}
            isLoading={isLoading}
            leftIcon={<FiUserPlus />}
          >
            Assign as Administrator
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const TenantModal: React.FC<TenantModalProps> = ({
  isOpen,
  onClose,
  tenant,
  onSave,
  isLoading,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TenantUpdateParams>({
    defaultValues: tenant
      ? {
          name: tenant.name,
          status: tenant.status as EntityStatus,
          logo: tenant.logo,
          main_color: tenant.main_color,
          payment_config: tenant.payment_config,
          reservation_config: tenant.reservation_config,
        }
      : {
          status: EntityStatus.ACTIVE,
        },
  });

  useEffect(() => {
    if (isOpen) {
      reset(
        tenant
          ? {
              name: tenant.name,
              status: tenant.status,
              logo: tenant.logo,
              main_color: tenant.main_color,
              payment_config: tenant.payment_config,
              reservation_config: tenant.reservation_config,
            }
          : {
              status: EntityStatus.ACTIVE,
            }
      );
    }
  }, [isOpen, tenant, reset]);

  const onSubmit = async (data: TenantUpdateParams) => {
    await onSave(data);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>{tenant ? 'Edit Tenant' : 'Add Tenant'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isInvalid={!!errors.name} isRequired>
                <FormLabel>Name</FormLabel>
                <Input
                  {...register('name', {
                    required: 'Name is required',
                  })}
                />
                {errors.name && <FormErrorMessage>{errors.name.message}</FormErrorMessage>}
              </FormControl>

              <FormControl isInvalid={!!errors.status} isRequired>
                <FormLabel>Status</FormLabel>
                <Select
                  {...register('status', {
                    required: 'Status is required',
                  })}
                >
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </Select>
                {errors.status && <FormErrorMessage>{errors.status.message}</FormErrorMessage>}
              </FormControl>

              <FormControl>
                <FormLabel>Logo URL</FormLabel>
                <Input {...register('logo')} />
              </FormControl>

              <FormControl>
                <FormLabel>Main Color</FormLabel>
                <Input 
                  {...register('main_color')}
                  placeholder="#FFFFFF" 
                  type="text"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              type="submit"
              isLoading={isLoading}
            >
              {tenant ? 'Update' : 'Create'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

const Tenants: React.FC = () => {
  const toast = useToast();
  const { user, token } = useAuthStore();
  const isSystem = user?.role === 'SYSTEM';
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { 
    isOpen: isAdminModalOpen, 
    onOpen: onAdminModalOpen, 
    onClose: onAdminModalClose 
  } = useDisclosure();
  
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTenant, setSelectedTenant] = useState<Tenant | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedTenantForAdmins, setSelectedTenantForAdmins] = useState<Tenant | null>(null);
  const [tenantAdmins, setTenantAdmins] = useState<User[]>([]);
  const [isLoadingAdmins, setIsLoadingAdmins] = useState(false);
  const [isAssigningAdmin, setIsAssigningAdmin] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);

  // Only SYSTEM users should access this page
  if (!isSystem) {
    return (
      <Box p={8} textAlign="center">
        <Heading size="md">Access Denied</Heading>
        <Box mt={4}>You don't have permission to manage tenants.</Box>
      </Box>
    );
  }

  const fetchTenants = async () => {
    setIsLoading(true);
    try {
      if (!token) throw new Error('Authentication token not available');
      apiClient.client.setToken(token);
      const response = await apiClient.tenants.listPaginated({ 
        page: currentPage, 
        limit: pageSize
      });
      setTenants(response.data);
      setTotalItems(response.pagination.total);
    } catch (error) {
      toast({
        title: 'Error fetching tenants',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      if (!token) throw new Error('Authentication token not available');
      apiClient.client.setToken(token);
      const response = await apiClient.users.listPaginated({ page: 1, limit: 2000, sort: 'updated_at', order: 'desc' as const });
      setAvailableUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: 'Error fetching users',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const fetchTenantAdmins = async (tenantId: string) => {
    setIsLoadingAdmins(true);
    try {
      if (!token) throw new Error('Authentication token not available');
      apiClient.client.setToken(token);
      // Here you would call your API to get tenant admins
      // For example: const response = await apiClient.tenants.getAdmins(tenantId);
      
      // Mocking the API call for now with fetching all users and filtering
      const response = await apiClient.users.listPaginated({ page: 1, limit: 2000, sort: 'created_at', order: 'desc' as const });
      // Filter users who are assigned to this tenant and have TENANT_ADMIN role
      const admins = response.data.filter(
        (user: User) => user.tenant_id === tenantId && user.role === 'TENANT_ADMIN'
      );
      setTenantAdmins(admins);
    } catch (error) {
      toast({
        title: 'Error fetching tenant admins',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setTenantAdmins([]);
    } finally {
      setIsLoadingAdmins(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchTenants();
      fetchAvailableUsers();
    }
  }, [currentPage, pageSize, searchQuery, token]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this tenant?')) {
      try {
        if (!token) throw new Error('Authentication token not available');
        apiClient.client.setToken(token);
        await apiClient.tenants.delete(id);
        toast({
          title: 'Tenant deleted',
          status: 'success',
          duration: 3000,
        });
        fetchTenants();
      } catch (error) {
        toast({
          title: 'Error deleting tenant',
          description: error instanceof Error ? error.message : 'Unknown error',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const handleEditTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    onOpen();
  };

  const handleAddTenant = () => {
    setSelectedTenant(undefined);
    onOpen();
  };

  const handleSaveTenant = async (data: TenantUpdateParams) => {
    setIsSaving(true);
    try {
      if (!token) throw new Error('Authentication token not available');
      apiClient.client.setToken(token);
      
      if (selectedTenant) {
        // Update existing tenant
        await apiClient.tenants.update(selectedTenant.id, data);
        toast({
          title: 'Tenant updated',
          status: 'success',
          duration: 3000,
        });
      } else {
        // Create new tenant
        await apiClient.tenants.create(data as TenantCreateParams);
        toast({
          title: 'Tenant created',
          status: 'success',
          duration: 3000,
        });
      }
      
      onClose();
      fetchTenants();
    } catch (error) {
      toast({
        title: selectedTenant ? 'Error updating tenant' : 'Error creating tenant',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleManageAdmins = (tenant: Tenant) => {
    setSelectedTenantForAdmins(tenant);
    fetchTenantAdmins(tenant.id);
    onAdminModalOpen();
  };

  const handleAssignAdmin = async (userId: string) => {
    if (!selectedTenantForAdmins) return;
    
    setIsAssigningAdmin(true);
    try {
      if (!token) throw new Error('Authentication token not available');
      apiClient.client.setToken(token);
      
      // Call the API to assign user as tenant admin
      await apiClient.tenants.assignUser(selectedTenantForAdmins.id, {
        user_id: userId,
        role: UserRole.TENANT_ADMIN
      });
      
      toast({
        title: 'User assigned as tenant admin',
        status: 'success',
        duration: 3000,
      });
      
      // Refresh the tenant admins list
      fetchTenantAdmins(selectedTenantForAdmins.id);
    } catch (error) {
      toast({
        title: 'Error assigning tenant admin',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsAssigningAdmin(false);
    }
  };

  const columns: Column<Tenant>[] = [
    {
      header: 'Name',
      accessor: 'name',
    },
    {
      header: 'Status',
      accessor: (tenant) => (
        <Badge colorScheme={tenant.status === 'ACTIVE' ? 'green' : 'red'}>
          {tenant.status}
        </Badge>
      ),
    },
    {
      header: 'Created',
      accessor: (tenant) => new Date(tenant.created_at).toLocaleDateString(),
    },
  ];

  const actionButtons = (tenant: Tenant) => (
    <>
      <IconButton
        aria-label="Manage tenant admins"
        icon={<FiUsers />}
        size="sm"
        colorScheme="teal"
        variant="ghost"
        onClick={() => handleManageAdmins(tenant)}
        mr={1}
      />
      <IconButton
        aria-label="Edit tenant"
        icon={<FiEdit2 />}
        size="sm"
        colorScheme="blue"
        variant="ghost"
        onClick={() => handleEditTenant(tenant)}
      />
      <IconButton
        aria-label="Delete tenant"
        icon={<FiTrash2 />}
        size="sm"
        colorScheme="red"
        variant="ghost"
        onClick={() => handleDelete(tenant.id)}
      />
    </>
  );

  return (
    <Box>
      <HStack justifyContent="space-between" mb={6}>
        <Heading size="lg">Tenants Management</Heading>
        <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={handleAddTenant}>
          Add Tenant
        </Button>
      </HStack>

      <DataTable
        columns={columns}
        data={tenants}
        keyField="id"
        isLoading={isLoading}
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSearch={handleSearch}
        actionButtons={actionButtons}
      />

      <TenantModal
        isOpen={isOpen}
        onClose={onClose}
        tenant={selectedTenant}
        onSave={handleSaveTenant}
        isLoading={isSaving}
      />

      {selectedTenantForAdmins && (
        <TenantAdminModal
          isOpen={isAdminModalOpen}
          onClose={onAdminModalClose}
          tenant={selectedTenantForAdmins}
          admins={tenantAdmins}
          onAssignAdmin={handleAssignAdmin}
          isLoading={isAssigningAdmin}
          availableUsers={availableUsers.filter(
            (user) => !tenantAdmins.some((admin) => admin.id === user.id)
          )}
        />
      )}
    </Box>
  );
};

export default Tenants; 